import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { styled } from "@mui/material/styles";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import { Menu, MenuItem, Divider } from "@mui/material";
import {
  Dashboard as DashboardIcon,
  ReportProblem as ReportProblemIcon,
  SupervisedUserCircle as SupervisedUserCircleIcon,
  MarkUnreadChatAlt as MarkUnreadChatAltIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon,
  Inventory as InventoryIcon,
  AddBusiness,
  SystemUpdateAltOutlined,
  MarkUnreadChatAltOutlined,
  Inventory2Outlined,
  Warehouse,
  ExitToApp as ExitToAppIcon,
  MonetizationOn,
} from "@mui/icons-material";
// import { themeBackgroundPrimaryColor } from "../utilities/colorTheme";
import { stringToColor } from "../utilities/commonUtils";
import UserRoleChip from "./commons/UserRoleChip";
import { privateApi } from "../api";
import { getAuth, signOut } from "firebase/auth";
import { toast } from "react-toastify";

// Removed unused color variables

const SidebarContainer = styled(Drawer)({
  display: "flex",
  flexDirection: "column",
  minHeight: "100%",
  width: 240,
  "& .MuiDrawer-paper": {
    width: 240,
    boxSizing: "border-box",
    background: "#fff", // White background
    borderRight: "1px solid #E2E8F0", // Light border
    marginTop: "72px",
    height: "calc(100vh - 72px)",
    boxShadow: "none",
  },
});

const SidebarContent = styled("div")({
  marginTop: "10px",
  padding: "10px 0", // Remove horizontal padding from container, add to items
  flex: 1,
  display: "flex",
  flexDirection: "column",
});

const ProfileSection = styled("div")({
  padding: "16px",
  borderTop: "1px solid #E2E8F0", // Light border
  marginTop: "auto",
  background: "#F8FAFC", // Slight contrast for profile area
});

const CompactProfile = styled(Button)({
  width: "auto",
  margin: "4px 10px",
  color: "#1E293B",
  background: "transparent",
  justifyContent: "flex-start",
  padding: "8px 12px",
  textTransform: "none",
  borderRadius: "8px",
  "&:hover": {
    background: "#fff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
});

const CompactProfileContent = styled("div")({
  display: "flex",
  alignItems: "center",
  width: "100%",
});

const CompactAvatar = styled(Avatar)({
  width: "32px",
  height: "32px",
  marginRight: "10px",
  border: "2px solid #fff",
  boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
});

const CompactProfileName = styled("div")({
  color: "#1E293B",
  fontSize: "0.9rem",
  fontWeight: 600,
  flex: 1,
  textAlign: "left",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

// LogoutButton removed

const StyledButton = styled(Button)(({ isdataactive }) => ({
  width: "auto", // Allow margin to work
  margin: "2px 10px", // Compact margins
  marginBottom: "2px",
  color: isdataactive ? "#2563EB" : "#64748B",
  background: isdataactive ? "#EFF6FF" : "transparent",
  justifyContent: "flex-start",
  padding: "8px 12px", // Standard padding
  minHeight: "40px", // Standard touch target
  borderRadius: "8px", // Slightly tighter radius
  textTransform: "none",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    background: isdataactive ? "#EFF6FF" : "#F8FAFC",
    color: isdataactive ? "#2563EB" : "#1E293B",
  },
}));

const StyledListItemIcon = styled("div")({
  color: "inherit",
  minWidth: "32px", // Compact icon spacing
  display: "flex",
  justifyContent: "center", // Center icon in its slot
  alignItems: "center",
  "& svg": {
    fontSize: "1.25rem", // Standard icon size
  },
});

const StyledListItemText = styled("div")({
  color: "inherit",
  marginLeft: "10px",
  textAlign: "left",
  fontWeight: 500, // Medium weight
  fontSize: "0.9rem", // 14.4px standard
});

const StyledDivider = styled("hr")({
  backgroundColor: "#E2E8F0",
  opacity: 1,
  margin: "4px 16px",
  border: "none",
  height: "1px",
});

const AppSidebar = ({ isMobile, setDrawerOpen }) => {
  const userType = useSelector((state) => state.userData.data.userType);
  const userRole = useSelector((state) => state.userData.data.role);
  const sessionUser = useSelector((state) => state.userData.data);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const displayUser = sessionUser;
  const [activeItems, setActiveItems] = useState({});
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);

  // Create logout handler
  const handleLogout = async () => {
    try {
      await privateApi.delete("users/logout");
      // Firebase logout
      const auth = getAuth();
      await signOut(auth);
      // After successful logout, you can perform any necessary cleanup or redirect
      localStorage.removeItem("_id"); // Clear user data
      dispatch({ type: "user/resetUserState" });
      dispatch({ type: "ticketManagement/resetToInitial" });
      dispatch({ type: "chats/resetToInitial" });
      dispatch({ type: "userManagement/resetToInitial" });
      navigate("/login"); // Redirect to login page
    } catch (error) {
      // Handle any error that occurs during logout
      toast.error("Logout failed. Please try again.");
    }
  };

  const handleProfileClick = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };

  useEffect(() => {
    const isItemActive = (path) => location.pathname === path;
    setActiveItems({
      "/": isItemActive("/"),
      "/Support": isItemActive("/Support"),
      "/enquiry-management": isItemActive("/enquiry-management"),
      "/tickets": isItemActive("/tickets"),
      "/User-Management": isItemActive("/User-Management"),
      "/Company-Management": isItemActive("/Company-Management"),
      "/Settings": isItemActive("/Settings"),
      "/Asset-Management": isItemActive("/Asset-Management"),
      "/inventory": isItemActive("/inventory"),
      "/franchise": isItemActive("/franchise"),
      "/export-assets-management": isItemActive("/export-assets-management"),
      "/it-amc-Management": isItemActive("/it-amc-Management"),
    });
  }, [location.pathname]);

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile && setDrawerOpen) setDrawerOpen(false);
  };

  const menuItems = [
    { path: "/", label: "Dashboard", icon: <DashboardIcon /> },
    {
      path: "/Support",
      label: "Support",
      icon: <MarkUnreadChatAltIcon />,
      roles: ["superAdmin", "coreAdmin", "supportAgent"],
    },
    {
      path: "/enquiry-management",
      label: "Enquiry Support",
      icon: <MarkUnreadChatAltOutlined />,
      roles: ["superAdmin", "coreAdmin", "supportAgent"],
    },
    { path: "/tickets", label: "Tickets", icon: <ReportProblemIcon /> },
    {
      path: "/User-Management",
      label: "Users",
      icon: <SupervisedUserCircleIcon />,
      roles: [
        "superAdmin",
        "customerAdmin",
        "technicalAdmin",
        "technician",
        "coreAdmin",
        "supportAgent",
      ],
    },
    {
      path: "/Company-Management",
      label: "Companies",
      icon: <BusinessIcon />,
      roles: [
        "superAdmin",
        "technicalAdmin",
        "technician",
        "coreAdmin",
        "supportAgent",
      ],
    },
    // {
    //   path: "/Renewals-Management",
    //   label: "Renawals",
    //   icon: <BusinessCenterTwoTone />,
    //   roles: [
    //     "superAdmin",
    //     "technicalAdmin",
    //     "technician",
    //     "coreAdmin",
    //     "supportAgent",
    //   ],
    // },
    {
      path: "/Asset-Management",
      label: "Assets",
      icon: <InventoryIcon />,
      userType: "Admin",
    },
    {
      path: "/inventory",
      label: "Inventory",
      icon: <Warehouse />,
      roles: ["superAdmin", "coreAdmin"],
    },
    {
      path: "/it-amc-Management",
      label: "IT AMC",
      icon: <Inventory2Outlined />,
      userType: "Admin",
    },
    {
      path: "/franchise",
      label: "Franchise",
      icon: <AddBusiness />,
      roles: ["coreAdmin"],
    },
    {
      path: "/Settings",
      label: "Settings",
      icon: <SettingsIcon />,
      roles: ["superAdmin", "coreAdmin"],
    },
    {
      path: "/export-assets-management",
      label: "Wholesale Assets",
      icon: <SystemUpdateAltOutlined />,
      permission: sessionUser.isEditExportAsset,
    },
    {
      path: "/sales",
      label: "Sales",
      icon: <MonetizationOn />,
      permission: sessionUser.accessSales || ["coreAdmin", "superAdmin"].includes(userRole),
    },
  ];

  const checkAccess = (item) => {
    if (item.roles && !item.roles.includes(userRole)) return false;
    if (item.userType && item.userType !== userType) return false;
    if (item.permission !== undefined && !item.permission) return false;
    return true;
  };

  const handleLogoutClick = () => {
    handleLogout();
    handleProfileClose();
    if (isMobile && setDrawerOpen) {
      setDrawerOpen(false);
    }
  };

  return (
    <SidebarContainer variant="permanent" anchor="left">
      <SidebarContent>
        <List>
          {menuItems.map(
            (item, index) =>
              checkAccess(item) && (
                <React.Fragment key={item.path}>
                  <StyledButton
                    component="li"
                    isdataactive={activeItems[item.path] ? "true" : undefined}
                    onClick={() => handleNavigation(item.path)}
                  >
                    <StyledListItemIcon>{item.icon}</StyledListItemIcon>
                    <StyledListItemText>{item.label}</StyledListItemText>
                  </StyledButton>
                  {index < menuItems.length - 1 && <StyledDivider />}
                </React.Fragment>
              )
          )}
        </List>

        {/* Profile Section at the bottom */}
        {displayUser && (
          <ProfileSection>
            <CompactProfile onClick={handleProfileClick}>
              <CompactProfileContent>
                <CompactAvatar
                  alt="User Avatar"
                  src={displayUser.photoURL}
                  style={{
                    backgroundColor: displayUser.photoURL
                      ? "transparent"
                      : displayUser.displayName
                        ? stringToColor(displayUser.displayName)
                        : stringToColor(
                          `${displayUser.firstName} ${displayUser.lastName}`
                        ),
                  }}
                >
                  {!displayUser.photoURL &&
                    (displayUser.displayName || displayUser.firstName)
                    ? displayUser.displayName
                      ? displayUser.displayName[0]
                      : displayUser.firstName[0]
                    : null}
                </CompactAvatar>
                <CompactProfileName>
                  {displayUser.displayName ||
                    `${displayUser.firstName} ${displayUser.lastName}`}
                </CompactProfileName>
              </CompactProfileContent>
            </CompactProfile>

            {/* Profile Details Menu */}
            <Menu
              anchorEl={profileAnchorEl}
              open={Boolean(profileAnchorEl)}
              onClose={handleProfileClose}
              PaperProps={{
                style: {
                  width: "300px",
                  marginTop: "5px",
                  padding: "7px",
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                },
              }}
            >
              <MenuItem
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "10px",
                  borderRadius: "10px",
                  border: "solid 1px #e0f7fa",
                  backgroundColor: "#e0f7fa !important",
                  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    backgroundColor: "#e0f7fa !important",
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                  },
                }}
              >
                <Avatar
                  alt="User Avatar"
                  src={displayUser.photoURL}
                  style={{
                    marginRight: "8px",
                    backgroundColor: displayUser.photoURL
                      ? "transparent"
                      : displayUser.displayName
                        ? stringToColor(displayUser.displayName)
                        : stringToColor(
                          `${displayUser.firstName} ${displayUser.lastName}`
                        ),
                  }}
                >
                  {!displayUser.photoURL &&
                    (displayUser.displayName || displayUser.firstName)
                    ? displayUser.displayName
                      ? displayUser.displayName[0]
                      : displayUser.firstName[0]
                    : null}
                </Avatar>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span style={{ fontWeight: "bold", marginBottom: "4px" }}>
                    {`${displayUser.displayName ||
                      `${displayUser.firstName} ${displayUser.lastName}`
                      }`?.slice(0, 25)}
                    {`${displayUser.displayName ||
                      `${displayUser.firstName} ${displayUser.lastName}`
                      }`?.length > 25
                      ? "..."
                      : ""}
                  </span>
                  {displayUser.company?.companyName && (
                    <span style={{ marginBottom: "4px", fontSize: "14px" }}>
                      {displayUser.company.companyName?.slice(0, 28)}
                      {displayUser.company.companyName?.length > 28
                        ? "..."
                        : ""}
                    </span>
                  )}
                  {displayUser.company?.companyCode && (
                    <span style={{ marginBottom: "4px", fontSize: "12px" }}>
                      {"Company Code: "}
                      {displayUser.company.companyCode}
                    </span>
                  )}
                  <UserRoleChip role={displayUser.role} />
                </div>
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={handleLogoutClick}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "8px",
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                  },
                }}
              >
                <ExitToAppIcon
                  fontSize="medium"
                  style={{ marginRight: "8px" }}
                />
                <span style={{ fontWeight: "bold" }}>Logout</span>
              </MenuItem>
            </Menu>

            {/* <LogoutButton
              onClick={handleLogoutClick}
              startIcon={<ExitToAppIcon />}
            >
              Logout
            </LogoutButton> */}
          </ProfileSection>
        )}
      </SidebarContent>
    </SidebarContainer>
  );
};

export default AppSidebar;
